import React from "react";
import { Link } from "react-router-dom";
import { Lock, Mail } from "lucide-react";
import { SUPPORT_EMAIL, legalLinks } from "../common/SiteFooter";
import { FOOTER_NOTE } from "./landingContent";
import { useLandingCopy } from "./landingCopy";

interface SocialLink {
  label: string;
  /** Profile URL. Links with an empty URL are not rendered. */
  href: string;
  icon: React.ReactNode;
}

const ICON_PROPS = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  "aria-hidden": true,
} as const;

// TODO(brand): add the real profile URLs — empty entries stay hidden rather than linking nowhere.
const SOCIAL_LINKS: SocialLink[] = [
  {
    label: "Instagram",
    href: "",
    icon: (
      <svg {...ICON_PROPS}>
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" />
      </svg>
    ),
  },
  {
    label: "X (Twitter)",
    href: "",
    icon: (
      <svg {...ICON_PROPS}>
        <path d="M4 4l16 16M20 4 4 20" />
      </svg>
    ),
  },
  {
    label: "TikTok",
    href: "",
    icon: (
      <svg {...ICON_PROPS}>
        <path d="M15 3v11.5a3.5 3.5 0 1 1-3.5-3.5" />
        <path d="M15 3c.4 2.4 2.2 4.2 4.5 4.5" />
      </svg>
    ),
  },
];

const LandingFooter: React.FC = () => {
  const { tr } = useLandingCopy();
  const socialLinks = SOCIAL_LINKS.filter((link) => link.href);
  // The translated note carries a fixed year; keep it current.
  const note = tr(FOOTER_NOTE).replace("2026", String(new Date().getFullYear()));

  return (
    <footer className="al-footer">
      <div className="al-footer__inner">
        <div className="al-footer__grid">
          <div>
            <div className="al-logo">
              <span className="al-logo__mark" aria-hidden="true" />
              Astria
            </div>
            <p className="al-footer__about">
              Guidance for your journey toward clarity and purpose. Upgrade, pause, or
              cancel your plan at any time.
            </p>
            {socialLinks.length > 0 && (
              <div className="al-footer__social">
                {socialLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    aria-label={link.label}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.icon}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <h3 className="al-footer__title">ข้อกำหนดทางกฎหมาย</h3>
            <ul className="al-footer__links">
              {legalLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to}>{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="al-footer__title">ติดต่อเรา</h3>
            <p className="al-footer__contact">
              Questions about billing, refunds, or your subscription? We typically
              reply within 2 business days.
            </p>
            <a className="al-footer__mail" href={`mailto:${SUPPORT_EMAIL}`}>
              <Mail size={15} aria-hidden="true" />
              {SUPPORT_EMAIL}
            </a>
          </div>
        </div>

        <div className="al-footer__end">
          <p>{note}</p>
          <div className="al-footer__badges">
            <span>
              <Lock size={14} aria-hidden="true" />
              ปลอดภัยตั้งแต่การออกแบบ
            </span>
            <span aria-hidden="true">·</span>
            <span>การชำระเงินดำเนินการผ่าน Stripe</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default LandingFooter;
